import { useRef, useState } from "react";
import { FileUp, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, eur, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";

export const MaterialImport = ({ dossierId, vatPercent = 21, onLines }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [labour, setLabour] = useState("");
  const [hours, setHours] = useState("");
  const [margin, setMargin] = useState(15);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const run = async () => {
    if (!file && text.trim().length < 10) return toast.error("Ajoutez un PDF ou écrivez la liste du matériel.");
    setBusy(true);
    setPreview(null);
    try {
      const form = new FormData();
      form.append("text", text);
      form.append("labour_amount", Number(labour) || 0);
      form.append("labour_hours", Number(hours) || 0);
      form.append("material_margin", Number(margin) || 0);
      form.append("vat_percent", vatPercent);
      if (dossierId) form.append("dossier_id", dossierId);
      if (file) form.append("file", file);
      const { data } = await studio.post("/quote-draft/material", form);
      setPreview(data);
      toast.success(`${data.lines.length} ligne(s) préparée(s).`);
    } catch (e) {
      toast.error(apiError(e, "Lecture impossible."));
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    onLines(preview.lines, preview);
    setPreview(null);
    setOpen(false);
    setText("");
    setFile(null);
    setLabour("");
    setHours("");
    toast.success("Lignes ajoutées au devis.");
  };

  return (
    <div className="mt-4 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#3A5CC8]" /> Devis depuis un PDF ou une liste de matériel
          </p>
          <p className="mt-1 text-sm text-[#5C6A87] leading-relaxed">
            Déposez le devis de votre fournisseur ou écrivez simplement le matériel avec les prix. Indiquez votre
            main-d'œuvre : l'assistant rédige les lignes et le texte de main-d'œuvre pour vous.
          </p>
        </div>
        <button type="button" data-testid="material-import-toggle" onClick={() => setOpen(!open)} className={btnGhost}>
          {open ? <X className="w-3.5 h-3.5" /> : <FileUp className="w-3.5 h-3.5" />} {open ? "Fermer" : "Importer"}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-3">
          <input
            ref={fileRef}
            data-testid="material-import-file"
            type="file"
            accept="application/pdf,.pdf,.txt,.csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-[#5C6A87] file:mr-3 file:rounded-full file:border-0 file:bg-[#EEF1FC] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-[#3A5CC8]"
          />
          <textarea
            data-testid="material-import-text"
            rows={5}
            className={inputCls}
            placeholder={"Ou écrivez la liste, une par ligne :\nChaudière Vaillant ecoTEC 24 kW — 1 pièce — 1450 €\nVase expansion 18 L — 1 — 65 €\nTubage inox 60/100 — 6 m — 22 €/m"}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Main-d'œuvre (€ HTVA)</span>
              <input data-testid="material-import-labour" type="number" step="0.01" min="0" className={`${inputCls} mt-1`} value={labour} onChange={(e) => setLabour(e.target.value)} />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Heures prévues</span>
              <input data-testid="material-import-hours" type="number" step="0.5" min="0" className={`${inputCls} mt-1`} value={hours} onChange={(e) => setHours(e.target.value)} />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Marge matériel (%)</span>
              <input data-testid="material-import-margin" type="number" step="1" min="0" max="200" className={`${inputCls} mt-1`} value={margin} onChange={(e) => setMargin(e.target.value)} />
            </label>
          </div>
          <button type="button" data-testid="material-import-run" disabled={busy} onClick={run} className={btnPrimary}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Préparer les lignes
          </button>

          {preview && (
            <div data-testid="material-import-preview" className="rounded-2xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
              <p className="text-sm font-semibold text-[#0F2645]">{preview.lines.length} ligne(s) proposée(s)</p>
              <div className="mt-2 space-y-1">
                {preview.lines.map((l, i) => (
                  <p key={i} className="text-xs text-[#5C6A87]">
                    <span className="font-semibold text-[#0F2645]">{l.quantity} {l.unit}</span> · {l.description} · {eur(l.unit_price)}
                    {l.cost_price ? ` (achat ${eur(l.cost_price)})` : ""}
                  </p>
                ))}
              </div>
              <p className="mt-2 text-sm font-bold text-[#0F2645]">Total HTVA {eur(preview.totals?.net)}</p>
              <button type="button" data-testid="material-import-apply" onClick={apply} className={`${btnPrimary} mt-3`}>
                Ajouter au devis
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
